import { useAtomValue } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';

import { trackAction } from '@consumer-frontend/shared';

import { CONTRACT_RUM_ACTIONS, ContractFlowStepT, ContractStepKeyT } from '../config';
import { contractStepAtom } from './atoms';

export type ContractFlowT = {
  canGoBack: boolean;
  currentIndex: number;
  goBack: () => void;
  goNext: () => void;
  goToStep: (key: ContractStepKeyT) => void;
  isLast: boolean;
  previousSteps: ContractFlowStepT[];
  stepNumber: number;
  steps: ContractFlowStepT[];
  totalSteps: number;
  /** Undefined when the :step segment matches no step in this flow. */
  current?: ContractFlowStepT;
};

export const useContractFlow = (): ContractFlowT => {
  const steps = useAtomValue(contractStepAtom);
  const { step } = useParams<{ step: string; }>();
  const navigate = useNavigate();

  const currentIndex = steps.findIndex(({ key }) => key === step);

  const goTo = (index: number) => navigate(`/contract/${steps[index].key}`);

  return {
    canGoBack: currentIndex > 0,
    // undefined when :step matches no step in this flow
    current: steps[currentIndex],
    currentIndex,
    goBack: () => {
      if (currentIndex > 0) {
        trackAction(CONTRACT_RUM_ACTIONS.NAVIGATION_BACK, {
          from: steps[currentIndex].key,
          to: steps[currentIndex - 1].key,
        });
        goTo(currentIndex - 1);
      }
    },
    goNext: () => {
      if (currentIndex > -1 && currentIndex < steps.length - 1) {
        trackAction(CONTRACT_RUM_ACTIONS.NAVIGATION_NEXT, {
          from: steps[currentIndex].key,
          to: steps[currentIndex + 1].key,
        });
        goTo(currentIndex + 1);
      }
    },
    goToStep: (key) => {
      if (steps.some((flowStep) => flowStep.key === key)) {
        navigate(`/contract/${key}`, { replace: true });
      }
    },
    isLast: currentIndex === steps.length - 1,
    previousSteps: currentIndex > 0 ? steps.slice(0, currentIndex) : [],
    stepNumber: currentIndex + 1,
    steps,
    totalSteps: steps.length,
  };
};
